/**
 * Reusable "Send to WhatsApp" button.
 *
 * Renders nothing if the WhatsApp module is disabled.
 * Hidden when the user lacks `whatsapp.send` permission.
 *
 * Props:
 *   templateKey: required — key from whatsappTemplates.DEFAULT_TEMPLATES
 *   doc:         the document/transaction to send
 *   party:       customer/vendor/staff master with phone
 *   extras:      passed through to PDF builder (for statement rows, period, etc.)
 *   variant:     'icon' | 'button' (default 'icon')
 *   size:        for icon variant, the icon size (default 18)
 *   label:       button text (button variant only); defaults to 'Send via WhatsApp'
 *   disabled:    extra disable check
 *   onSent:      callback(result) when send succeeds
 *   onError:     callback(error)
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { FaWhatsapp } from 'react-icons/fa';
import { FiLoader } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import usePagePermissions from '../hooks/usePagePermissions';
import { sendDocumentViaWhatsApp } from '../utils/whatsappHelper';

const SendWhatsAppButton = ({
  templateKey, doc, party, extras, variant = 'icon', size = 18, label = 'Send via WhatsApp',
  disabled, onSent, onError, confirmBeforeSend = true, title = 'Send via WhatsApp'
}) => {
  const { activeCompany, isModuleEnabled } = useAuth();
  const { canEdit: canSend } = usePagePermissions('whatsapp', 'send');
  const [busy, setBusy] = useState(false);

  if (!isModuleEnabled('whatsapp_connect')) return null;
  if (!canSend) return null;
  if (!party || (!party.phone && !party.mobile)) {
    // Show disabled state with tooltip explaining why
    return variant === 'icon' ? (
      <IconBtn title="No phone number on file" disabled style={{ opacity: 0.4 }}>
        <FaWhatsapp size={size} />
      </IconBtn>
    ) : (
      <Btn disabled style={{ opacity: 0.4 }} title="No phone number on file">
        <FaWhatsapp /> {label}
      </Btn>
    );
  }

  const handleClick = async (e) => {
    e?.stopPropagation?.();
    if (busy || disabled) return;
    const partyName = party.companyName || party.vendorName || party.staffName || party.fullName || party.name || 'recipient';
    const partyPhone = party.phone || party.mobile;
    if (confirmBeforeSend && !window.confirm(`Send via WhatsApp to ${partyName} (${partyPhone})?`)) return;
    setBusy(true);
    try {
      const result = await sendDocumentViaWhatsApp({
        templateKey, doc, party, activeCompany, extras: extras || {}
      });
      onSent?.(result);
      if (!onSent) alert('Sent via WhatsApp ✓');
    } catch (err) {
      console.error('[whatsapp send]', err);
      onError?.(err);
      if (!onError) alert(`WhatsApp send failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'icon') {
    return (
      <IconBtn title={title} onClick={handleClick} disabled={busy || disabled}>
        {busy ? <FiLoader className="spin" /> : <FaWhatsapp size={size} />}
      </IconBtn>
    );
  }
  return (
    <Btn onClick={handleClick} disabled={busy || disabled} title={title}>
      {busy ? <FiLoader className="spin" /> : <FaWhatsapp />} {busy ? 'Sending…' : label}
    </Btn>
  );
};

export default SendWhatsAppButton;

const IconBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  color: #25D366;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: rgba(37, 211, 102, 0.1); }
  &:disabled { cursor: not-allowed; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  background: #25D366; color: #fff; border: none;
  padding: 8px 14px; border-radius: 6px; font-size: 14px; font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: #1ebd5b; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;
